import { enemyBulletTimeScale } from '../bombs';
import { CULL_MARGIN, DT, PF_HH, PF_HW } from '../constants';
import { Motion, Rank, type EnemyHookContext } from '../defs';
import { Ev } from '../events';
import { FEEL } from '../FeelConstants';
import { cos } from '../math';
import type { World } from '../World';

/** Contexte passé au hook d'échappatoire. Réutilisé d'un appel à l'autre :
 *  aucune allocation dans la boucle chaude. */
const _ctx: { playerX: number; playerY: number; x: Float32Array; y: Float32Array } = {
  playerX: 0,
  playerY: 0,
  x: new Float32Array(0),
  y: new Float32Array(0),
};
function hookCtx(w: World): EnemyHookContext {
  _ctx.playerX = w.player.x;
  _ctx.playerY = w.player.y;
  _ctx.x = w.enemies.x;
  _ctx.y = w.enemies.y;
  return _ctx;
}

const OUT_X = PF_HW + CULL_MARGIN;
const OUT_Y = PF_HH + CULL_MARGIN;

/** Accélération de chute d'un ramassable, en u/s². */
const PICKUP_GRAVITY = 900;
/** Vitesse de chute maximale, en u/s.
 *
 *  Dimensionnée sur la géométrie : un ramassable doit traverser les ~750 u qui
 *  séparent un ennemi du joueur BIEN AVANT d'expirer (12 s). À 320 u/s, il met
 *  2,3 s — large. Trop lent, il meurt en route ; trop rapide, il file sous le
 *  joueur avant que l'aimantation ait le temps d'agir. */
const PICKUP_FALL_MAX = 320;

/** Un ennemi entre à 40 % de sa vitesse et l'atteint en 0,5 s (PLAN.md §6.5).
 *  Sans ça il « surgit » sur le joueur, et la mort qui suit passe pour de
 *  l'arbitraire plutôt que pour une erreur de lecture. */
function entryMul(age: number): number {
  const t = age / 0.5;
  return t >= 1 ? 1 : 0.4 + 0.6 * t;
}

/** Fraction de la vitesse de piqué appliquée pendant l'APPROCHE (Motion.Dive).
 *
 *  Constante de moteur et non paramètre de contenu, délibérément. Le Dard
 *  n'enseigne qu'une chose — « lent, lent, lent, PIQUÉ » — et cette chose est
 *  un RAPPORT, pas une vitesse. L'exposer au contenu permettrait de régler un
 *  Dard à 0,9 : il descendrait alors presque aussi vite qu'il plonge, la pause
 *  cesserait de se voir, et l'ennemi deviendrait un Linear rapide sans que
 *  personne ne s'aperçoive d'avoir supprimé sa raison d'être. Un rapport de 1
 *  pour 4 se lit à coup sûr. */
const DIVE_APPROACH = 0.25;

/* Note de style, valable dans tous les systèmes : on lit une valeur dans un
   local, on calcule, on réécrit. Deux raisons — TypeScript refuse `a[i]! += x`
   comme cible d'affectation, et un accès de tableau typé est un accès mémoire
   borné qu'on ne veut pas répéter trois fois dans la même expression. */

/* ── Ressort de recul ────────────────────────────────────────────────────────
   Un oscillateur harmonique amorti : `x'' = -k·x - c·x'`. Réglé légèrement
   SOUS-amorti (ζ ≈ 0,55) pour un unique petit dépassement — l'ennemi revient,
   franchit un peu sa place, puis se stabilise. Sur-amorti, il rentrerait sans
   caractère ; trop sous-amorti, il vibrerait comme un ressort de dessin animé.

   `k = ω²`, `c = 2ζω` avec ω ≈ 34 rad/s (période ~0,18 s) : le recul se joue et
   se résorbe en ~0,3 s, franc mais lisible. Ces deux nombres se règlent à
   l'œil — c'est du ressenti, pas de l'équilibrage. */
const KB_STIFF = 1150;
const KB_DAMP = 38;
/** Seuil de position sous lequel on considère le recul éteint (unités). */
const KB_EPS = 0.05;
/** Seuil de vitesse correspondant (u/s). Le ressort ne se fige que quand les
 *  DEUX sont franchis — un ressort à l'arrêt au point de rappel a encore de la
 *  vitesse, et le couper sur la seule position le laisserait reculer sans fin. */
const KB_EPS_V = 1.5;

export function motionSystem(w: World): void {
  /* ── ennemis ──────────────────────────────────────────────────────────
     Trajectoires codées en dur au J1 ; elles deviendront déclaratives avec la
     machine à contenu (J4). Itération EN ARRIÈRE : la boucle peut libérer. */
  const en = w.enemies;
  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);
    const age = en.age[s]! + DT;
    en.age[s] = age;

    // Décompte du télégraphe : l'ennemi est en place, visible, et devient
    // dangereux à l'expiration.
    const tel = en.telegraph[s]!;
    if (tel > 0) en.telegraph[s] = tel - DT > 0 ? tel - DT : 0;

    const m = entryMul(age);

    /* ⚠️ On repart de la position de TRAJECTOIRE, recul retiré. Les mouvements
       intégrants (Linear, Sine, Hover…) relisent `en.x`/`en.y` pour avancer :
       si le recul y était resté baké, il s'intégrerait au tick suivant et une
       simple bousculade deviendrait une dérive permanente. On le retire ici, on
       le ré-applique amorti en fin de traitement. */
    let x = en.x[s]! - en.kbx[s]!;
    let y = en.y[s]! - en.kby[s]!;

    // Les PARTIES sont traitées dans une seconde passe, une fois tous les
    // porteurs déplacés (voir en fin de fonction).
    if (en.parent[s]! >= 0) continue;

    /* Trajectoire pilotée par les DONNÉES. Le moteur connaît quatre formes de
       mouvement ; le contenu choisit laquelle et avec quels paramètres. Ajouter
       un ennemi ne demande plus d'ouvrir ce fichier — c'est le critère de fin
       du jalon J4. */
    const def = w.defs.enemies[en.kind[s]!];
    if (def) {
      const mo = def.motion;
      switch (mo.kind) {
        case Motion.Linear:
          y -= mo.speed * DT * m;
          break;
        case Motion.Sine:
          y -= mo.speed * DT * m;
          x += cos(age * mo.frequency) * mo.amplitude * DT * m;
          break;
        case Motion.Hover:
          // Descend jusqu'à sa station, puis oscille latéralement.
          if (y > mo.holdY) y -= mo.speed * DT * m;
          else x += cos(age * mo.frequency) * mo.amplitude * DT;
          break;
        case Motion.Strafe:
          // Traverse à hauteur constante après être descendu à sa hauteur de
          // croisière. La direction dépend du côté d'apparition.
          if (y > mo.holdY) y -= mo.speed * DT * m;
          else x += (x < 0 ? 1 : -1) * mo.speed * DT * m;
          break;
        case Motion.Dive: {
          // Front du PIQUÉ : le télégraphe (pause) s'achève ce tick. On le
          // signale UNE fois pour le rugissement du plongeon (audio).
          const holdT = mo.hold ?? 0;
          if (age >= holdT && age - DT < holdT) {
            w.events.push(Ev.EnemyDive, 0, x, y, 0);
          }
          /* Deux conditions, et il faut LES DEUX (voir Motion.Dive dans defs).
             Être arrivé à sa station garantit que le piqué part toujours de la
             même altitude ; l'horloge garantit la pause qui le télégraphie.

             L'ordre du test compte peu, mais le fait qu'il ne puisse jamais
             repasser en approche, si : une fois `y` sous `holdY`, la première
             condition reste vraie pour toujours et l'âge ne décroît pas. Le
             piqué est donc irréversible, ce qui est exactement ce qu'on veut
             d'un piqué. */
          if (y <= mo.holdY && age >= (mo.hold ?? 0)) {
            y -= mo.speed * DT * m;
          } else {
            y -= mo.speed * DIVE_APPROACH * DT * m;
            // Écrêtage à la station. Sans lui, le Dard dépasserait `holdY` de
            // la fraction de tick restante et plongerait d'une altitude
            // légèrement différente selon l'instant de son apparition — la
            // dispersion même que ce mouvement existe pour supprimer.
            if (y < mo.holdY) y = mo.holdY;
          }
          break;
        }
        case Motion.Anchor:
          // Descend à sa station, puis PLUS RIEN. Pas d'oscillation : c'est ce
          // qui le fait basculer de « adversaire » à « décor » dans l'œil du
          // joueur (voir Motion.Anchor dans defs).
          if (y > mo.holdY) {
            y -= mo.speed * DT * m;
            if (y < mo.holdY) y = mo.holdY;
          }
          break;
        default:
          break;
      }

      // Échappatoire documentée (PLAN.md §3.3) : dernier recours, jamais le
      // chemin par défaut. Si plus de 30 % des définitions l'utilisent, c'est
      // le schéma déclaratif qui est faux, pas le contenu.
      if (def.onUpdate) {
        en.x[s] = x;
        en.y[s] = y;
        def.onUpdate(s, age, DT, hookCtx(w));
        x = en.x[s]!;
        y = en.y[s]!;
      }
    }

    /* Retour du recul par RESSORT AMORTI, puis ré-application sur la trajectoire
       fraîche. `x`/`y` sont la position PROPRE ; `en.x`/`en.y` la position
       VISIBLE, celle que lisent le rendu et la collision.

       Le coup a déplacé d'un coup (dans `collisionSystem`) : ici, à chaque tick,
       une force de rappel `-k·x` ramène l'ennemi vers sa place et un
       amortissement `-c·v` freine le mouvement. Résultat : il repart de l'arrêt
       (accélération), passe par un maximum de vitesse, ralentit à l'arrivée
       (décélération) et dépasse légèrement avant de se stabiliser — le « il
       cherche à reprendre sa place » d'un vrai choc. La décroissance
       exponentielle d'avant n'avait que la moitié de la courbe : vitesse
       maximale à l'impact, puis rien que du ralentissement.

       Intégration semi-implicite (vitesse d'abord, position ensuite) : stable
       pour un ressort à ce pas de temps, là où l'Euler explicite divergerait. */
    let kx = en.kbx[s]!;
    let ky = en.kby[s]!;
    let kvx = en.kbvx[s]!;
    let kvy = en.kbvy[s]!;
    kvx += (-KB_STIFF * kx - KB_DAMP * kvx) * DT;
    kvy += (-KB_STIFF * ky - KB_DAMP * kvy) * DT;
    kx += kvx * DT;
    ky += kvy * DT;
    // Retour au repos EXACT sous un double seuil position + vitesse : sans le
    // seuil de vitesse, le ressort oscillerait indéfiniment à amplitude
    // microscopique, du bruit permanent dans l'empreinte.
    if (kx < KB_EPS && kx > -KB_EPS && kvx < KB_EPS_V && kvx > -KB_EPS_V) {
      kx = 0;
      kvx = 0;
    }
    if (ky < KB_EPS && ky > -KB_EPS && kvy < KB_EPS_V && kvy > -KB_EPS_V) {
      ky = 0;
      kvy = 0;
    }
    en.kbx[s] = kx;
    en.kby[s] = ky;
    en.kbvx[s] = kvx;
    en.kbvy[s] = kvy;
    en.x[s] = x + kx;
    en.y[s] = y + ky;

    /* ⚠️ Un ennemi qui vient de naître AU-DESSUS du cadre est en train d'ENTRER
       en scène — il ne faut pas l'éliminer.

       Bug mesuré sur 500 graines : `spawnFromDef` fait apparaître entre
       Y = 660 et 750, alors que la limite d'élimination est à 740. Environ
       10 % de TOUS les spawns étaient donc supprimés au tick suivant leur
       naissance, avant d'avoir parcouru un pixel. Invisible sur du popcorn —
       on ne compte pas les mites — mais fatal sur une entité unique : le boss
       aurait été absent une partie sur dix, sans le moindre message.

       La marge de 0,5 s correspond à l'accélération d'entrée : passé ce délai,
       un ennemi encore au-dessus du cadre est bien reparti vers le haut, et là
       il faut le libérer. */
    const enteringFromTop = y > OUT_Y && age <= 0.5;
    if (!enteringFromTop && (y < -OUT_Y || y > OUT_Y || x < -OUT_X || x > OUT_X)) {
      /* ⚠️ Pénalité de FUITE. Retour de jeu : « il faudrait des pénalités si je
         laisse fuir des ennemis ». Un ennemi MENAÇANT (rang troupe ou plus,
         télégraphe terminé) qui franchit le BAS de l'écran a traversé tout le
         terrain sans être abattu : on retire sa valeur du score. Double perte —
         on ne gagne pas ce qu'il valait ET on paie de l'avoir laissé passer,
         ce qui pousse à l'agressivité au lieu d'attendre.

         Seul le bas compte : un ennemi qui sort par un côté (Rôdeur) ou remonte
         n'a rien « laissé fuir ». Et jamais le popcorn — on ne compte pas les
         Mites, les punir de passer serait du bruit. */
      if (def && def.rank >= Rank.Standard && en.telegraph[s]! <= 0 && y < -OUT_Y) {
        const lost = en.score[s]!;
        w.score = w.score - lost > 0 ? w.score - lost : 0;
        w.events.push(Ev.EnemyEscaped, lost, en.x[s]!, en.y[s]!, 0);
      }
      en.free(s);
    }
  }

  /* ── parties destructibles : SECONDE PASSE ────────────────────────────
     ⚠️ Obligatoirement après le déplacement de tous les porteurs.

     Régression vécue : traitées dans la même boucle, les parties étaient
     repositionnées selon l'ORDRE INTERNE DU POOL — souvent avant que leur
     porteur ait bougé. Elles accusaient alors exactement un tick de retard,
     soit 1,67 unité à 200 u/s. Visible à l'œil comme des canons qui traînent
     derrière le vaisseau, et impossible à attribuer sans connaître l'ordre
     d'itération.

     Une partie n'a aucune trajectoire propre : elle est boulonnée. */
  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);
    if (en.parent[s]! < 0) continue;

    if (!en.hasLivingParent(s)) {
      // Porteur détruit, ou son slot recyclé par un autre ennemi : l'orpheline
      // n'a plus de raison d'exister.
      en.free(s);
      continue;
    }
    const p = en.parent[s]!;
    en.x[s] = en.x[p]! + en.offX[s]!;
    en.y[s] = en.y[p]! + en.offY[s]!;
  }

  /* ── projectiles ennemis ───────────────────────────────────────────────
     ⚠️ Le seul endroit du moteur où le TEMPS des projectiles ennemis se dilate
     (bombe TEMPS MORT). On multiplie le PAS d'intégration, jamais les vitesses
     stockées : une balle née pendant la fenêtre est ralentie comme les autres,
     et l'expiration ne demande aucune restauration — donc aucun slot à
     retrouver après recyclage. Hors fenêtre, le facteur vaut exactement 1 et
     l'arithmétique est inchangée. */
  const ebScale = enemyBulletTimeScale(w.player.bomb, w.player.bombFxT);
  const eb = w.eb;
  for (let i = eb.count - 1; i >= 0; i--) {
    const s = eb.at(i);
    const x = eb.x[s]! + eb.vx[s]! * DT * ebScale;
    const y = eb.y[s]! + eb.vy[s]! * DT * ebScale;
    eb.x[s] = x;
    eb.y[s] = y;
    if (x < -OUT_X || x > OUT_X || y < -OUT_Y || y > OUT_Y) eb.free(s);
  }

  /* ── projectiles joueur ──────────────────────────────────────────────── */
  const pb = w.pb;
  for (let i = pb.count - 1; i >= 0; i--) {
    const s = pb.at(i);
    const x = pb.x[s]! + pb.vx[s]! * DT;
    const y = pb.y[s]! + pb.vy[s]! * DT;
    pb.x[s] = x;
    pb.y[s] = y;
    if (x < -OUT_X || x > OUT_X || y < -OUT_Y || y > OUT_Y) pb.free(s);
  }

  /* ── ramassables : dérive, puis aimantation vers le joueur ───────────── */
  const pu = w.pickups;
  const p = w.player;
  for (let i = pu.count - 1; i >= 0; i--) {
    const s = pu.at(i);
    pu.age[s] = pu.age[s]! + DT;

    let vx = pu.vx[s]!;
    let vy = pu.vy[s]!;
    const dx = p.x - pu.x[s]!;
    const dy = p.y - pu.y[s]!;
    const d2 = dx * dx + dy * dy;

    if (!p.dead && d2 < FEEL.pickupMagnetRadius * FEEL.pickupMagnetRadius) {
      // Aimantation. Le frottement s'applique AUSSI ici — sans dissipation,
      // l'objet dépasse le joueur, est rappelé, redépasse, et orbite sans
      // jamais être ramassé.
      const d = Math.sqrt(d2);
      if (d > 0.001) {
        const pull = 900 * DT;
        vx += (dx / d) * pull;
        vy += (dy / d) * pull;
      }
      vx *= 0.985;
      vy *= 0.985;
    } else {
      /* ⚠️ DÉRIVE LIBRE — le frottement ne s'applique QU'À L'HORIZONTALE.

         L'appliquer aussi à la verticale était une erreur, et elle rendait le
         butin inatteignable : la chute plafonnait à ~33 u/s, soit 22 secondes
         pour parcourir les 750 unités qui séparent un ennemi du joueur. Or les
         ramassables expirent au bout de 12 s. Ils mouraient donc TOUS en route,
         sans jamais entrer dans le rayon d'aimantation — mesuré à 434 u du
         joueur au plus près, pour un rayon de 260.

         L'horizontale, elle, doit bien être amortie : elle porte la gerbe de
         l'explosion, qui doit s'ouvrir puis se calmer. La verticale porte la
         chute, qui doit rester franche. */
      vx *= 0.985;
      vy -= PICKUP_GRAVITY * DT;
      if (vy < -PICKUP_FALL_MAX) vy = -PICKUP_FALL_MAX;
    }

    const x = pu.x[s]! + vx * DT;
    const y = pu.y[s]! + vy * DT;
    pu.vx[s] = vx;
    pu.vy[s] = vy;
    pu.x[s] = x;
    pu.y[s] = y;

    if (y < -OUT_Y) pu.free(s);
  }
}
